import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import "./HourlyPlatesStackedChart.css";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, ReferenceArea
} from "recharts";
import { useTranslation } from "react-i18next";

// --- UNIVERSAL CUSTOM TOOLTIP (Defined outside to prevent React "static flag" errors) ---
const CustomTooltip = ({ active, payload, label, isDashboard, t }) => {
  if (!active || !payload || !payload.length) return null;

  let totalAllowed = 0;
  let totalBlocked = 0;
  const areaData = {};

  payload.forEach((item) => {
    if (item.dataKey.includes("notAllowed") || item.dataKey === "notAllowed") {
      totalBlocked += item.value;
    } else {
      totalAllowed += item.value;
    }

    if (!isDashboard) {
      const areaName = item.dataKey.replace("_allowed", "").replace("_notAllowed", "");
      if (!areaData[areaName]) {
        areaData[areaName] = { allowed: 0, blocked: 0, color: item.color };
      }
      if (item.dataKey.includes("_allowed")) areaData[areaName].allowed = item.value;
      if (item.dataKey.includes("_notAllowed")) areaData[areaName].blocked = item.value;
    }
  });

  return (
    <div style={{
      backgroundColor: "var(--ez-card-bg, #fff)",
      padding: "12px",
      borderRadius: "8px",
      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
      fontSize: "13px",
      border: "1px solid var(--bs-border-color)"
    }}>
      <p style={{ margin: "0 0 8px 0", fontWeight: "bold", borderBottom: "1px solid var(--bs-border-color)" }}>{label}</p>

      {isDashboard ? (
        <div style={{ marginBottom: "8px" }}>
          <div style={{ color: "var(--ez-orange-light)" }}>{t("Authorized")}: {totalAllowed}</div>
          <div style={{ color: "#ef4444" }}>{t("Unauthorized")}: {totalBlocked}</div>
        </div>
      ) : (
        Object.entries(areaData).map(([name, vals]) => (
          <div key={name} style={{ display: "flex", justifyContent: "space-between", gap: "20px", marginBottom: "4px" }}>
            <span style={{ color: vals.color, fontWeight: "600" }}>{name}:</span>
            <span>{vals.allowed} / <span style={{ color: "#ef4444", fontWeight: "bold" }}>{vals.blocked}</span></span>
          </div>
        ))
      )}

      <div style={{ marginTop: "8px", paddingTop: "8px", borderTop: "2px dashed #f3f4f6", fontWeight: "bold", display: "flex", justifyContent: "space-between" }}>
        <span>TOTAL:</span>
        <span>{totalAllowed + totalBlocked} <small style={{ fontWeight: "normal", color: "#9ca3af" }}>({totalAllowed}/{totalBlocked})</small></span>
      </div>
    </div>
  );
};

function HourlyPlatesStackedChart({ data, areas = [], areaColorMap = {}, selectedHour, onHourClick, onLegendClick }) {
  const { t } = useTranslation();
  const colors = ["#fb923c", "#10b981", "#3b82f6", "#8b5cf6", "#06b6d4"];
  const isDashboard = areas.length === 0;

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setContainerWidth(Math.floor(entry.contentRect.width));
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const xAxisInterval = isMobile ? 3 : 1;

  const handleChartClick = (e) => {
    if (!onHourClick || !containerRef.current) return;
    if (e.target.closest('.recharts-legend-wrapper')) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = (e.clientX ?? e.changedTouches?.[0]?.clientX) - rect.left;
    const plotLeft = 35; // margin.left(5) + yAxisWidth(30)
    const plotRight = containerWidth - 5; // margin.right(5)
    if (x < plotLeft || x > plotRight) return;
    const hour = Math.min(23, Math.max(0, Math.floor(((x - plotLeft) / (plotRight - plotLeft)) * 24)));
    onHourClick(areas[0], hour);
  };

  // Optimized Axis rendering for tight spaces
  const renderXAxis = () => (
    <XAxis
      dataKey="hour"
      interval={xAxisInterval}
      tick={{ fontSize: 10 }}
    />
  );

  const renderYAxis = (hide = false) => (
    <YAxis
      hide={hide}           // Completely removes the axis space
      tick={{ fontSize: 10 }}
      width={hide ? 0 : 30} // Forces the width to 0 if hidden
    />
  );

  if (isDashboard) {
    return (
      <div ref={containerRef} style={{ width: "100%", height: isMobile ? 280 : 350 }}>
        {containerWidth > 0 && (
          <AreaChart
            width={containerWidth}
            height={isMobile ? 280 : 350}
            data={data}
            margin={{ top: 5, right: 0, left: 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#7d7d7d5d" />
            {renderXAxis()}
            {renderYAxis()}
            <Tooltip content={<CustomTooltip isDashboard={true} t={t} />} />
            <Area type="monotone" dataKey="notAllowed" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} isAnimationActive={true} />
            <Area type="monotone" dataKey="allowed" stackId="1" stroke="var(--ez-orange-light)" fill="var(--ez-orange-light)" fillOpacity={0.4} isAnimationActive={true} />
          </AreaChart>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ width: "100%", height: 400, cursor: onHourClick ? 'pointer' : 'default' }} onClick={handleChartClick}>
      {containerWidth > 0 && (
        <BarChart
          width={containerWidth}
          height={400}
          data={data}
          barGap={2}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#7d7d7d5d" />
          {renderXAxis()}
          {renderYAxis()}
          {selectedHour != null && (
            <ReferenceArea x1={`${selectedHour}:00`} x2={`${selectedHour}:00`} fill="rgba(0,0,0,0.18)" ifOverflow="visible" />
          )}
          <Tooltip content={<CustomTooltip isDashboard={false} t={t} />} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
          <Legend iconType="circle" wrapperStyle={{ paddingTop: '10px', cursor: onLegendClick ? 'pointer' : 'default' }} onClick={(data) => onLegendClick?.(data.value)} />
          {areas.flatMap((areaName, index) => [
            <Bar key={`${areaName}_allowed`} dataKey={`${areaName}_allowed`} stackId={areaName} name={areaName} fill={areaColorMap[areaName] ?? colors[index % colors.length]} isAnimationActive={true} />,
            <Bar key={`${areaName}_notAllowed`} dataKey={`${areaName}_notAllowed`} stackId={areaName} fill="#ef4444" radius={[2, 2, 0, 0]} legendType="none" isAnimationActive={true} />
          ])}
        </BarChart>
      )}
    </div>
  );
}

export default HourlyPlatesStackedChart;
